import type { AxiosError } from 'axios';
import qs from 'qs';

import { API_URL } from '@/config/env';
import { conformanceRequest } from '@/config/msalConfig';
import { conformanceApi } from '@/lib/api';
import { hasCheckpointVersionValue } from '@/lib/checkpointKeys';
import { getToken } from '@/lib/utils';

import type { IObject } from '@/domains/core/core.types';

import type {
  Evaluation,
  EvaluationAssessmentDataItem,
  EvaluationCheckpoint,
  EvaluationsResponse,
  EvaluationStatusResponse,
  TriggerEvaluationRequest,
} from './evaluation.types';

const normalizeCheckpoint = (
  checkpoint: EvaluationCheckpoint,
): EvaluationCheckpoint => {
  return {
    ...checkpoint,
    items: Array.isArray(checkpoint.items) ? checkpoint.items : [],
    assessmentData: Array.isArray(checkpoint.assessmentData)
      ? checkpoint.assessmentData
      : [],
    exceptionDetails: checkpoint.exceptionDetails ?? null,
  };
};

export const fetchEvaluations = (filters: IObject) => {
  return conformanceApi.get<EvaluationsResponse>(`${API_URL}/evaluations`, {
    params: { ...filters },
    paramsSerializer: (params) => {
      return qs.stringify(params, { arrayFormat: 'repeat', encode: false });
    },
  });
};

export const exportEvaluationsCsv = (filters: IObject) => {
  return conformanceApi.get<Blob>(`${API_URL}/evaluations/export`, {
    params: { ...filters },
    paramsSerializer: (params) => {
      return qs.stringify(params, { arrayFormat: 'repeat', encode: false });
    },
    responseType: 'blob',
  });
};

export const fetchEvaluationExcel = async (
  evaluationId: string,
  view: string = 'default',
) => {
  const token = await getToken(conformanceRequest);
  return fetch(
    `${API_URL}/jobs/results/${evaluationId}?${new URLSearchParams({
      Format: 'Excel',
      view,
    })}`,
    {
      method: 'GET',
      headers: {
        authorization: `Bearer ${token}`,
        accept:
          'application/json, application/vnd.openxmlformats-officedocument.spreadsheet, text/plain, */*',
      },
    },
  );
};

export const fetchCheckpointAssessment = (
  recordId: string,
  checkpointId: string,
  checkpointVersion?: string | number | null,
) => {
  const hasCheckpointVersion = hasCheckpointVersionValue(checkpointVersion);

  return conformanceApi.get<{
    data: EvaluationAssessmentDataItem[];
  }>(`${API_URL}/evaluations/${recordId}/assessments/${checkpointId}`, {
    params: hasCheckpointVersion
      ? {
          CheckpointVersion: `${checkpointVersion}`,
        }
      : undefined,
  });
};

export const triggerEvaluation = ({
  bitId,
  ...rest
}: TriggerEvaluationRequest) => {
  return conformanceApi.post<{ jobId: string; statusUrl: string }>(
    `${API_URL}/jobs/${bitId}`,
    rest,
    {
      headers: {
        'x-orr-automation-platform': 'ORR Portal',
      },
    },
  );
};

export const getEvaluationJobStatus = (jobId: string) => {
  return conformanceApi.get<EvaluationStatusResponse>(
    `${API_URL}/jobs/${jobId}/status`,
  );
};

export const fetchEvaluation = async ({
  evaluationId,
  view,
}: {
  evaluationId: string;
  view: string;
}) => {
  const enrichEvaluation = (evaluation: Evaluation | undefined) => {
    if (!evaluation) return;

    evaluation.bitId =
      evaluation.bitId ||
      evaluation.metadata?.bitId ||
      evaluation.request?.bitId ||
      '';
    evaluation.applicationName =
      evaluation.applicationName || evaluation.metadata?.name || '';
    evaluation.divisionName =
      evaluation.divisionName || evaluation.metadata?.division || '';
    evaluation.environmentNames =
      evaluation.environmentNames?.length > 0
        ? evaluation.environmentNames
        : (evaluation.request?.environmentNames ?? []);
    evaluation.platform =
      evaluation.platform ||
      evaluation.request?.headers?.['x-orr-automation-platform'] ||
      '';
    evaluation.args = evaluation.args || evaluation.request?.args || {};
    evaluation.cloudAccounts = Array.isArray(evaluation.cloudAccounts)
      ? evaluation.cloudAccounts
      : [];
    evaluation.extraSourceCloudAccounts = Array.isArray(
      evaluation.extraSourceCloudAccounts,
    )
      ? evaluation.extraSourceCloudAccounts
      : [];
    evaluation.checkpoints = Array.isArray(evaluation.checkpoints)
      ? evaluation.checkpoints.map(normalizeCheckpoint)
      : [];

    if (!evaluation.extraSourceCloudAccounts) return;

    const hasAccounts = evaluation.extraSourceCloudAccounts.length > 0;
    evaluation.isWKTenant =
      hasAccounts &&
      evaluation.extraSourceCloudAccounts.every(
        (acc) =>
          acc.parentCloudAccountId ===
            '8ac76c91-e7f1-41ff-a89c-3553b2da2c17' ||
          acc.parentCloudAccountId === '297538631377',
      );
  };

  const getEvaluation = (requestedView: string) =>
    conformanceApi.get<Evaluation>(`${API_URL}/evaluations/${evaluationId}`, {
      params: { Format: 'Json', view: requestedView },
    });

  try {
    const response = await getEvaluation(view);
    enrichEvaluation(response.data);
    return response;
  } catch (error) {
    const status = (error as AxiosError).response?.status;

    if (status === 404 && view !== 'default') {
      // The evaluation exists, but the requested view has no checkpoints in it
      // (e.g. a newly-created/empty view such as `cloud_economics`, or a view that
      // postdates this evaluation). Reuse the evaluation header from the default
      // view but show an EMPTY checkpoint set — instead of falling back to the
      // default view's own checkpoints, which would misleadingly render the wrong
      // (previous) data for the selected view.
      const base = await getEvaluation('default');
      enrichEvaluation(base.data);
      if (base.data) {
        base.data.checkpoints = [];
        base.data.score = 0;
      }
      return base;
    }

    if (status === 404) {
      return {
        data: undefined,
      };
    }

    throw error;
  }
};
